/**
 * CoverageCardPhoto: the "From a photo" panel on Add insurance (22k2).
 *
 * The person photographs the front of the insurance card and, when it
 * carries more, the back; "Read the card" sends both to the photo reader
 * (kind 'insurance_card'). What the reader finds fills the form's boxes
 * through the dialog's own seam (`onRead`), which fills only boxes that
 * are empty or still hold a value an earlier read put there, and reports
 * back what it did. This panel then says it in plain words: check each
 * box; what was kept as typed; what the program does not show; that a
 * Medicare card's Part B is its own row; or that nothing was read.
 *
 * Nothing is saved here. Add insurance saves the boxes, and only then are
 * the photos filed in Documents, when "Keep the photos in Documents" is on
 * (the dialog files them; this panel only holds the tick).
 *
 * Opened from Documents ("Read into Insurance") the panel starts on that
 * document's image as the front and reads at once. The photo is already
 * filed, so the keep tick is not drawn, and a back photo added here is
 * read but not kept; the panel says so.
 *
 * House copy: no engine words; warm, plain, second person.
 */

import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Camera } from 'lucide-react';
import { Button } from '@/components/primitives/Button';
import { ErrorNotice } from '@/components/ErrorNotice';
import { ReadingIndicator } from '@/components/notes/ReadingIndicator';
import { photoIntakeKeyOf } from '@/lib/errorCopy';
import {
  CARD_TEXT_FIELDS,
  extractInsuranceCard,
  prepareCardImages,
  type InsuranceCardRead,
  type IntakeImage,
} from '@/lib/photoIntakeApi';

/** What a read did to the form, in the boxes' own labels. */
export interface CardReadOutcome {
  /** Boxes that kept a value the person typed. */
  kept: string[];
  /** Values read that the chosen program does not show. */
  hidden: string[];
  /** No program is chosen, so none of the card's boxes show yet. */
  needsProgram: boolean;
  /** The Medicare card shows Part B beside Part A. */
  partB: boolean;
}

export interface CoverageCardPhotoProps {
  /** A front photo handed over from Documents: the panel opens on it and
   *  reads at once, and the keep tick is not drawn. */
  startFront?: File;
  /** Fills the form from a read and says what it did. */
  onRead: (card: InsuranceCardRead, photos: IntakeImage[]) => CardReadOutcome;
  keep: boolean;
  onKeepChange: (keep: boolean) => void;
}

/** True when the read found nothing at all. */
function readNothing(card: InsuranceCardRead): boolean {
  return card.program === null && CARD_TEXT_FIELDS.every((f) => card[f] === null);
}

function list(labels: string[]): string {
  return labels.join(', ');
}

export function CoverageCardPhoto({ startFront, onRead, keep, onKeepChange }: CoverageCardPhotoProps): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const fromDocument = startFront !== undefined;
  const frontRef = useRef<HTMLInputElement>(null);
  const backRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(fromDocument);
  const [front, setFront] = useState<File | null>(startFront ?? null);
  const [back, setBack] = useState<File | null>(null);
  const [frontGap, setFrontGap] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<CardReadOutcome | null>(null);
  const [nothing, setNothing] = useState(false);

  const readMut = useMutation<{ card: InsuranceCardRead; photos: IntakeImage[] }, unknown, { front: File; back: File | null }>({
    mutationFn: async (vars) => {
      const photos = await prepareCardImages(vars.front, vars.back);
      const result = await extractInsuranceCard(tenantSlug, { images: photos, clientId });
      return { card: result.card, photos };
    },
    onMutate: () => {
      setErrorKey(null);
      setOutcome(null);
      setNothing(false);
    },
    onSuccess: ({ card, photos }) => {
      if (readNothing(card)) {
        setNothing(true);
        return;
      }
      setOutcome(onRead(card, photos));
    },
    onError: (e) => setErrorKey(photoIntakeKeyOf(e)),
  });

  function read(): void {
    if (front === null) {
      setFrontGap(true);
      return;
    }
    readMut.mutate({ front, back });
  }

  // Opened from Documents: read the handed-over image at once.
  const startedRef = useRef(false);
  useEffect(() => {
    if (!fromDocument || startedRef.current || startFront === undefined) return;
    startedRef.current = true;
    readMut.mutate({ front: startFront, back: null });
  }, [fromDocument, startFront, readMut]);

  function onPick(side: 'front' | 'back', e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0] ?? null;
    // Reset the input so choosing the same file again fires change again.
    e.target.value = '';
    if (file === null) return;
    if (side === 'front') {
      setFront(file);
      setFrontGap(false);
    } else {
      setBack(file);
    }
  }

  if (!open) {
    return (
      <div className="mb-2">
        <Button
          variant="secondary"
          size="sm"
          icon={<Camera size={14} aria-hidden="true" />}
          data-testid="coverage-photo-door"
          onClick={() => setOpen(true)}
        >
          From a photo
        </Button>
      </div>
    );
  }

  const reading = readMut.isPending;

  return (
    <section
      aria-label="Read the insurance card from a photo"
      data-testid="coverage-photo-panel"
      className="mb-3 flex flex-col gap-3 rounded-base border border-sage-800/[12%] p-3"
    >
      <p className="text-sm text-ink-soft">
        Take a photo of the front of the card, and the back if it has more on it. You'll see the boxes filled in to check before you add the insurance.
      </p>

      <input
        ref={frontRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid="coverage-photo-front"
        onChange={(e) => onPick('front', e)}
      />
      <input
        ref={backRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid="coverage-photo-back"
        onChange={(e) => onPick('back', e)}
      />

      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" size="sm" data-testid="coverage-photo-front-choose" onClick={() => frontRef.current?.click()}>
            {front === null ? 'Front photo' : 'Change the front photo'}
          </Button>
          {front !== null && (
            <span className="text-sm text-ink break-all" data-testid="coverage-photo-front-name">
              {fromDocument && front === startFront ? `From Documents: ${front.name}` : front.name}
            </span>
          )}
        </div>
        {frontGap && (
          <p role="alert" className="text-xs text-pepper-deep" data-testid="coverage-photo-front-gap">
            Add a photo of the front of the card.
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" data-testid="coverage-photo-back-choose" onClick={() => backRef.current?.click()}>
            {back === null ? 'Back photo (if it has more)' : 'Change the back photo'}
          </Button>
          {back !== null && (
            <span className="text-sm text-ink break-all" data-testid="coverage-photo-back-name">
              {back.name}
            </span>
          )}
        </div>
      </div>

      {!fromDocument && (
        <label className="inline-flex min-h-11 cursor-pointer items-center gap-2.5 text-sm text-sage-800">
          <input
            type="checkbox"
            data-testid="coverage-photo-keep"
            className="h-5 w-5 shrink-0 accent-sage-700"
            checked={keep}
            onChange={(e) => onKeepChange(e.target.checked)}
          />
          Keep the photos in Documents
        </label>
      )}

      {reading ? (
        <ReadingIndicator kind="photo" testId="coverage-photo-reading" />
      ) : (
        <div>
          <Button variant="primary" size="sm" data-testid="coverage-photo-read" onClick={read}>
            {outcome !== null || nothing ? 'Read the card again' : 'Read the card'}
          </Button>
        </div>
      )}

      {errorKey !== null && (
        <ErrorNotice surface="insurance_card_extract" code={errorKey} data-testid="coverage-photo-error" />
      )}

      {nothing && (
        <p className="text-sm text-ink" data-testid="coverage-photo-nothing">
          Try a clearer, well-lit photo of the card.
        </p>
      )}

      {outcome !== null && (
        <div className="flex flex-col gap-1.5 text-sm text-ink" aria-live="polite">
          <p data-testid="coverage-photo-check" className="font-medium text-sage-800">
            Check each box against the card before you add it.
          </p>
          {outcome.kept.length > 0 && (
            <p data-testid="coverage-photo-kept">Kept what you typed in: {list(outcome.kept)}.</p>
          )}
          {outcome.hidden.length > 0 && (
            <p data-testid="coverage-photo-hidden">
              {outcome.needsProgram
                ? `Choose the program to see what was read for: ${list(outcome.hidden)}.`
                : `Also read, but not used for this program: ${list(outcome.hidden)}. Change the program if that is wrong.`}
            </p>
          )}
          {outcome.partB && (
            <p data-testid="coverage-photo-part-b">
              This card also shows Medicare Part B. Add Part B as its own insurance after this one.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
